import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { Claim, EvidenceVersion } from "@edufadder/shared-types";
import { Chip, type ChipTone } from "@edufadder/ui";
import { useAuth } from "../lib/auth-context.js";
import * as api from "../lib/api.js";
import { ApiError } from "../lib/api.js";
import * as s from "../styles.js";

const statusTone: Record<string, ChipTone> = {
  draft: "neutral",
  evidence_review: "blue",
  approved: "amber",
  published: "green",
  superseded: "neutral",
  retired: "red",
};

export function ClaimVersionsPage(): JSX.Element {
  const { claimId } = useParams<{ claimId: string }>();
  const { me, hasPermission } = useAuth();
  const [claim, setClaim] = useState<Claim | null>(null);
  const [versions, setVersions] = useState<EvidenceVersion[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!claimId) return;
    try {
      const [c, v] = await Promise.all([api.getClaim(claimId), api.listVersions(claimId)]);
      setClaim(c);
      setVersions(v);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't load this claim's versions.");
    }
  }, [claimId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function doSubmit(versionId: string): Promise<void> {
    setActionError(null);
    try {
      await api.submitForReview(versionId);
      await load();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "Couldn't submit for review.");
    }
  }

  async function doApprove(versionId: string): Promise<void> {
    setActionError(null);
    try {
      await api.approveVersion(versionId);
      await load();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "Couldn't approve this version.");
    }
  }

  async function doVerifySource(sourceId: string): Promise<void> {
    setActionError(null);
    try {
      await api.verifySource(sourceId);
      await load();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "Couldn't verify this source.");
    }
  }

  if (error) return <div style={s.container}><p style={s.errorText}>{error}</p></div>;
  if (!claim || !versions) return <div style={s.container}><p style={s.mutedText}>Loading…</p></div>;

  return (
    <div style={s.container}>
      <h1 style={s.heading1}>{claim.label}</h1>
      <p style={s.mutedText}>
        <Link to={`/claims/${claim.id}`}>View the public report page</Link>
      </p>
      {hasPermission("claims:write") && (
        <p>
          <Link to={`/claims/${claim.id}/versions/new`} style={{ ...s.buttonPrimary, textDecoration: "none", display: "inline-block" }}>
            New version
          </Link>
        </p>
      )}
      {actionError && <p style={s.errorText}>{actionError}</p>}

      {versions.length === 0 && <p style={s.mutedText}>No evidence versions yet.</p>}
      {versions.map((v) => {
        const isAuthor = v.author_id === me?.id;
        const canSubmit = v.status === "draft" && isAuthor;
        // Approval requires every linked source verified — and no one may approve
        // their own version — see services/api/app/services/evidence.py. The UI
        // still lets you click; the API is the real enforcement, this just avoids
        // showing a button that would obviously fail.
        const canApprove = v.status === "evidence_review" && !isAuthor && hasPermission("claims:approve");
        return (
          <div key={v.id} style={s.card}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Chip tone={statusTone[v.status] ?? "neutral"}>{v.status}</Chip>
              <span style={s.mutedText}>{v.evidence_strength}</span>
            </div>
            <p style={{ marginTop: 8 }}>{v.summary}</p>
            {v.sources.length > 0 && (
              <ul style={{ listStyle: "none", padding: 0 }}>
                {v.sources.map((src) => (
                  <li key={src.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0" }}>
                    <span>
                      {src.citation} {src.role && <span style={s.mutedText}>({src.role})</span>}
                    </span>
                    <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <Chip tone={src.verification_status === "verified" ? "green" : "amber"}>{src.verification_status}</Chip>
                      {src.verification_status !== "verified" && hasPermission("sources:verify") && (
                        <button type="button" style={s.buttonSecondary} onClick={() => void doVerifySource(src.id)}>
                          Verify
                        </button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              {canSubmit && (
                <button type="button" style={s.buttonPrimary} onClick={() => void doSubmit(v.id)}>
                  Submit for review
                </button>
              )}
              {canApprove && (
                <button type="button" style={s.buttonPrimary} onClick={() => void doApprove(v.id)}>
                  Approve
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
